import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  styled,
  Typography,
  Button,
  TextField,
  FormHelperText,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import BannerImage from "../professionalTournaments/BannerImage";
import { BEAUTY_PAGEANT_LABEL } from "../../../utils/homeFlow";
import {
  HOME_CHOOSER_BANNER_WIDTH,
  HOME_CHOOSER_BANNER_HEIGHT,
} from "../../../utils/advertisementBannerSpec";

const CustomTextarea = styled(TextField)(() => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    color: "black",
    padding: "10px 15px",
    "&:hover": {
      backgroundColor: "transparent",
      border: "1px solid #e5e7eb",
    },
    "&.Mui-focused": {
      backgroundColor: "transparent",
      border: "1px solid #e5e7eb",
      outline: "none",
    },
  },

  "& .MuiInputBase-input": {
    padding: "0",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    "&:hover": {
      border: "none",
    },
  },
}));

/**
 * @param usedCategoryIds - category ids that already have a banner (create mode hides these)
 * @param currentCategoryId - when editing, keep current category selectable
 */
const HomeBannerForm = ({
  formik,
  edited,
  usedCategoryIds = [],
  currentCategoryId = null,
}) => {
  const homeBannerCategories = useSelector(
    (state) => state.homeBannerCategories
  );

  const availableCategories = useMemo(() => {
    const all = homeBannerCategories.data?.data || [];
    if (edited) {
      return all.filter(
        (c) =>
          !usedCategoryIds.includes(Number(c.id)) ||
          Number(c.id) === Number(currentCategoryId)
      );
    }
    return all.filter((c) => !usedCategoryIds.includes(Number(c.id)));
  }, [
    homeBannerCategories.data,
    usedCategoryIds,
    edited,
    currentCategoryId,
  ]);

  return (
    <Box className="page_inner">
      <form onSubmit={formik.handleSubmit}>
        <Box className="create-banner-form professional-tournament-form">
          <Box className="category form-control">
            <Typography className="label">Banner Image</Typography>
            <FormHelperText sx={{ mb: 1 }}>
              Any size is auto-resized to fill the home chooser cards. You do
              not need to crop or resize the file first.
            </FormHelperText>
            <Box className="Banner_img_preview">
              <BannerImage
                formik={formik}
                edited={edited}
                autoFit={{
                  width: HOME_CHOOSER_BANNER_WIDTH,
                  height: HOME_CHOOSER_BANNER_HEIGHT,
                }}
              />
              {formik.touched.image && formik.errors.image ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.image}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>
          </Box>

          <Box className="category form-control">
            <Box className="category_field">
              <Typography className="label">Card title (optional)</Typography>
              <CustomTextarea
                variant="outlined"
                name="banner_name"
                placeholder="e.g. My Football App"
                value={formik.values.banner_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <FormHelperText>
                Large title on the home chooser card. Leave blank to use the
                category name.
              </FormHelperText>
              {formik.touched.banner_name && formik.errors.banner_name ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.banner_name}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>
          </Box>

          <Box className="category form-control">
            <Box className="category_field">
              <Typography className="label">Category</Typography>
              <CustomTextarea
                select
                variant="outlined"
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={edited}
              >
                <MenuItem value="default">Select a Category</MenuItem>
                {homeBannerCategories.loading ? (
                  <MenuItem disabled value="">
                    <CircularProgress size={18} sx={{ color: "#ef364e" }} />
                  </MenuItem>
                ) : availableCategories.length > 0 ? (
                  availableCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    {edited
                      ? "No categories found"
                      : "All categories already have a banner"}
                  </MenuItem>
                )}
              </CustomTextarea>
              {!edited ? (
                <FormHelperText>
                  Short names work best for the card tag (e.g. Football,
                  {BEAUTY_PAGEANT_LABEL}). Only one banner per category.
                </FormHelperText>
              ) : (
                <FormHelperText>
                  Category is fixed for this banner. Change the image or title
                  to update it.
                </FormHelperText>
              )}
              {formik.touched.category && formik.errors.category ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.category}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>
          </Box>

          <Box className="category form-control">
            <Box className="category_field">
              <Typography className="label">Sort order</Typography>
              <CustomTextarea
                variant="outlined"
                name="sort_order"
                type="number"
                placeholder="0"
                value={formik.values.sort_order}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <FormHelperText>
                Lower numbers appear first on the home experience chooser
                (left card = 0).
              </FormHelperText>
              {formik.touched.sort_order && formik.errors.sort_order ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.sort_order}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>
          </Box>

          <Box className="category form-control">
            <Button className="pt-create" type="submit">
              {edited ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default HomeBannerForm;
